import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { PageHeader } from '@/components/app/PageHeader';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const EMPTY = { name: '', mobile: '', email: '', tax_id: '', address: '' };

export default function AdminOwners() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(() => api.get('/owners').then((r) => setItems(r.data)).catch(() => toast.error('Failed to load owners')), []);
  useEffect(() => { load(); }, [load]);

  const startCreate = () => { setForm(EMPTY); setCreating(true); };
  const startEdit = (o) => { setEdit(o); setForm({ name: o.name || '', mobile: o.mobile || '', email: o.email || '', tax_id: o.tax_id || '', address: o.address || '' }); };

  const save = async () => {
    if (!form.name.trim()) return toast.error('Name is required');
    try {
      if (edit) {
        await api.put(`/owners/${edit.id}`, form);
        toast.success('Owner updated');
      } else {
        await api.post('/owners', form);
        toast.success('Owner added');
      }
      setEdit(null); setCreating(false); setForm(EMPTY); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const remove = async () => {
    try {
      await api.delete(`/owners/${confirmDelete.id}`);
      toast.success('Owner deleted');
      setConfirmDelete(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Cannot delete (in use)'); }
  };

  const isOpen = creating || !!edit;
  const closeForm = (v) => { if (!v) { setCreating(false); setEdit(null); setForm(EMPTY); } };

  return (
    <div>
      <PageHeader title="Owners" subtitle="Property owners onboarded to the system" actions={
        <Button onClick={startCreate} data-testid="admin-owners-add"><Plus className="h-4 w-4 mr-1" /> Add owner</Button>
      } />

      <Card className="p-0 overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="bg-muted/60">
            <tr>
              <th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">Name</th>
              <th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">Mobile</th>
              <th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">Email</th>
              <th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">Tax ID</th>
              <th className="px-3 py-2 text-xs uppercase text-muted-foreground text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-8 text-center text-muted-foreground">No owners yet. Click "Add owner" to create the first one.</td></tr>
            )}
            {items.map((o) => (
              <tr key={o.id} className="border-t hover:bg-muted/40" data-testid={`owner-row-${o.id}`}>
                <td className="px-3 py-2 font-medium">{o.name}</td>
                <td className="px-3 py-2">{o.mobile || '—'}</td>
                <td className="px-3 py-2">{o.email || '—'}</td>
                <td className="px-3 py-2 font-mono text-xs">{o.tax_id || '—'}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => startEdit(o)} data-testid={`owner-edit-${o.id}`} aria-label="Edit owner">
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-8 px-2 text-destructive hover:text-destructive" onClick={() => setConfirmDelete(o)} data-testid={`owner-delete-${o.id}`} aria-label="Delete owner">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={isOpen} onOpenChange={closeForm}>
        <DialogContent className="sm:max-w-[520px] max-h-[92dvh] overflow-y-auto">
          <DialogHeader><DialogTitle>{edit ? 'Edit owner' : 'Add owner'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><Label>Name <span className="text-destructive">*</span></Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="owner-form-name" /></div>
            <div><Label>Mobile</Label><Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} data-testid="owner-form-mobile" /></div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="owner-form-email" /></div>
            <div className="sm:col-span-2"><Label>Tax ID (PAN)</Label><Input value={form.tax_id} onChange={(e) => setForm({ ...form, tax_id: e.target.value })} data-testid="owner-form-tax" /></div>
            <div className="sm:col-span-2"><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="owner-form-address" /></div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => closeForm(false)} className="w-full sm:w-auto">Cancel</Button>
            <Button onClick={save} className="w-full sm:w-auto" data-testid="owner-form-save">{edit ? 'Save changes' : 'Add owner'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete owner?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove <strong>{confirmDelete?.name}</strong> from the system. Owners that still have properties cannot be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="owner-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
